import { useCallback } from 'react';
import { Flex, Text, Button } from '.';
import { theme } from '../../design-system';
import { logmarData } from '../../../../helpers/data';
import { shouldForwardProps } from '../../helpers';
import { useEffect, useState } from 'react';
import { usePostMessage } from '../../../../context/messageContext';
import { useMaxLogmar } from '../../../../helpers/useMaxLogmar';
import { useTestContext } from '../../../../context/testContext';

const LogmarSelector = ({ logmarCharacters, selectedLogmar, setSelectedLogmar, ...props }) => {
  const { customPropsObj } = shouldForwardProps(props);
  const [highlightedCharacter, setHighlightedCharacter] = useState(null);
  const postMessage = usePostMessage();
  const updateMaxLogmar = useMaxLogmar();
  const {
    testData: { maxPermittedLogmar },
  } = useTestContext();

  useEffect(() => {
    if (selectedLogmar) {
      const selectedItem = document.getElementById(`logmar-id-${selectedLogmar.id}`)?.getBoundingClientRect();
      const container = document.getElementById('logmar-selector-container')?.getBoundingClientRect();

      const selectionIsVisible = selectedItem?.top >= container?.top && selectedItem?.bottom <= container?.bottom;

      if (!selectionIsVisible)
        document.getElementById(`logmar-id-${selectedLogmar.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    }
  }, [selectedLogmar]);

  const handleCharacterHighlight = useCallback(
    (charId) => {
      postMessage({ type: 'highlightLogmarCharacter', content: charId });
      return setHighlightedCharacter(charId);
    },
    [postMessage],
  );

  useEffect(() => {
    handleCharacterHighlight(null);
  }, [selectedLogmar, handleCharacterHighlight]);

  useEffect(() => {
    updateMaxLogmar();
  }, [updateMaxLogmar]);

  useEffect(() => {
    if (selectedLogmar?.id < maxPermittedLogmar?.id) setSelectedLogmar(maxPermittedLogmar);
  }, [selectedLogmar, setSelectedLogmar, maxPermittedLogmar]);

  return (
    <Flex
      column
      alignItems='center'
      borderRadius='4px'
      height='60vh'
      width='60vw'
      minHeight='150px'
      minWidth='325px'
      maxWidth='500px'
      maxHeight='600px'
      backgroundColor='#FBFBFB'
      border='0.5px solid #cccccc'
      boxShadow='inset 0px -0.5px 0px #EEEEEE'
      position='relative'
      overflow='auto'
      scrollSnapType='y mandatory'
      scrollSnapStop='always'
      {...customPropsObj}
      id='logmar-selector-container'
    >
      {logmarData.map((logmar) => {
        const charsForThisLogmar = logmarCharacters?.[logmar.id]?.split(' ');
        const logmarSelected = logmar?.id === selectedLogmar?.id;
        const logmarDisabled = logmar?.id < maxPermittedLogmar?.id;
        return (
          <Flex
            width='100%'
            padding='1rem'
            justifyContent='center'
            key={`logmar-id-${logmar.id}`}
            id={`logmar-id-${logmar.id}`}
            scrollSnapAlign='start'
            onClick={logmarDisabled ? null : props.onClick ? props.onClick : () => setSelectedLogmar(logmar)}
            backgroundColor={logmarSelected ? '#e0dfdf' : logmarDisabled ? '#f5aeae' : 'transparent'}
            border={`1px solid ${logmarSelected ? theme.colors.primary : '#fbfbfb'}`}
          >
            <Flex width={!logmarCharacters ? '100%' : '60%'} justifyContent={'center'} gap={logmarCharacters ? '3rem' : null}>
              <Flex justifyContent='center' width='1rem'>
                <Text>{logmar.value.toFixed(1)}</Text>
              </Flex>
              {props.testInProgress && (
                <Button
                  backgroundColor={theme.colors.negativeDark}
                  padding='0 0.5rem'
                  visibility={highlightedCharacter?.includes(logmar.id) ? 'visible' : 'hidden'}
                  onClick={() => handleCharacterHighlight(null)}
                >
                  {`\u2716`}
                </Button>
              )}

              <Flex gap='0.75rem'>
                {charsForThisLogmar?.map((char) => {
                  const isHighlighted = highlightedCharacter === `logmar-${logmar.id}-char-${char}`;
                  return (
                    <Text
                      key={`logmar-${logmar.id}-char-${char}`}
                      id={`logmar-${logmar.id}-char-${char}`}
                      padding='0 0.4rem'
                      onClick={logmar.id === selectedLogmar.id ? (e) => handleCharacterHighlight(e.target.id) : null}
                      style={{
                        border: `1px solid ${isHighlighted ? 'black' : 'transparent'}`,
                        backgroundColor: isHighlighted ? '#ddffdd' : 'transparent',
                      }}
                    >
                      {char}
                    </Text>
                  );
                })}
              </Flex>
            </Flex>
          </Flex>
        );
      })}
    </Flex>
  );
};

export default LogmarSelector;
